import { useEffect, useState } from "react";
import api from "@/lib/api";
import { KpiCard, PageHeader, Panel, EmptyState } from "@/components/admin/shared";
import { TomorrowReminders } from "@/components/admin/TomorrowReminders";
import { MorningBriefing } from "@/components/admin/MorningBriefing";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { waLink, waMessage } from "@/lib/constants";
import { Phone, MessageCircle, Check, Clock } from "lucide-react";
import { toast } from "sonner";

const BUCKETS = [
  { key: "today", label: "Today" },
  { key: "overdue", label: "Overdue" },
  { key: "upcoming", label: "Upcoming" },
  { key: "completed", label: "Completed" },
];

export default function Followups() {
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);

  const load = async () => {
    const [f, t] = await Promise.all([api.get("/followups"), api.get("/team")]);
    setItems(f.data); setTeam(t.data);
  };
  useEffect(() => { load(); }, []);

  const teamName = (id) => team.find((t) => t.member_id === id)?.name || "Unassigned";
  const counts = BUCKETS.reduce((a, b) => ({ ...a, [b.key]: items.filter((i) => i.bucket === b.key).length }), {});
  const complete = async (id) => { await api.patch(`/followups/${id}`, { status: "Completed" }); toast.success("Marked complete"); load(); };

  const List = ({ bucket }) => {
    const rows = items.filter((i) => i.bucket === bucket);
    if (!rows.length) return <EmptyState text="Nothing here" />;
    return (
      <div className="space-y-2">
        {rows.map((f) => (
          <div key={f.followup_id} data-testid="followup-row" className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-slate-50">
            <div className="flex-1 min-w-[160px]">
              <p className="font-semibold text-[#081226]">{f.name}</p>
              <p className="text-xs text-slate-400 font-mono-num">{f.mobile} · {f.interest}</p>
            </div>
            <div className="text-xs text-slate-500"><Clock size={13} className="inline mr-1" />{f.followup_date ? new Date(f.followup_date).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</div>
            <div className="text-xs font-semibold text-slate-600">{teamName(f.assigned_to)}</div>
            <div className="flex items-center gap-1">
              <a href={waLink(f.mobile, waMessage(f.name, f.interest))} target="_blank" rel="noreferrer" className="p-2 text-[#25D366]" data-testid="followup-whatsapp-btn"><MessageCircle size={17} /></a>
              <a href={`tel:${f.mobile}`} className="p-2 text-[#081226]"><Phone size={17} /></a>
              {f.status !== "Completed" && <button onClick={() => complete(f.followup_id)} data-testid="followup-complete-btn" className="p-2 text-emerald-600" title="Mark complete"><Check size={18} /></button>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <PageHeader title="Follow-ups & Tasks" subtitle={`${items.length} scheduled follow-ups`} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="fu-kpi-today" label="Today" value={counts.today || 0} accent="#0284C7" />
        <KpiCard testid="fu-kpi-overdue" label="Overdue" value={counts.overdue || 0} accent="#D95D1E" />
        <KpiCard testid="fu-kpi-upcoming" label="Upcoming" value={counts.upcoming || 0} accent="#E8752A" />
        <KpiCard testid="fu-kpi-completed" label="Completed" value={counts.completed || 0} accent="#10B981" />
      </div>
      <MorningBriefing className="mb-6" />
      <TomorrowReminders className="mb-6" />
      <Panel>
        <Tabs defaultValue="today">
          <TabsList className="mb-4">{BUCKETS.map((b) => <TabsTrigger key={b.key} value={b.key} data-testid={`fu-tab-${b.key}`}>{b.label} ({counts[b.key] || 0})</TabsTrigger>)}</TabsList>
          {BUCKETS.map((b) => <TabsContent key={b.key} value={b.key}><List bucket={b.key} /></TabsContent>)}
        </Tabs>
      </Panel>
    </div>
  );
}
